import React, {
    useCallback,
    useContext,
    useMemo,
    useState,
    Fragment,
    useRef,
    useEffect,
} from 'react';
import classNames from 'classnames';
import { Tooltip, Icon } from '@Sage/hoi-poi-ui';
import EmojiMenu from './EmojiMenu';
import { RichTextContext } from '../index';

const ToolbarItem = ({ className: classNameProp, classNameActive, color, item, hint, active }) => {
    const { editor, setShowingMenuPopover } = useContext(RichTextContext);
    const [visible, setVisible] = useState(false);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const className = classNames(classNameProp, { [classNameActive]: active });
    const emojiMenuRef = useRef(null);

    useEffect(() => {
        if (item === 'emoji' && popoverVisible) {
            const handleKeyDown = (event) => {
                emojiMenuRef?.current?.onKeyDown(event);
            };
            document.addEventListener('keydown', handleKeyDown, true);

            return () => {
                document.removeEventListener('keydown', handleKeyDown, true);
            };
        }
    }, [item, popoverVisible]);

    const tooltipProps = useMemo(
        () => ({
            placement: 'top',
            content: <Fragment>{hint}</Fragment>,
        }),
        [hint],
    );

    const iconProps = useMemo(() => {
        let name, onClick;

        const commonProps = {
            color,
            className,
        };

        switch (item) {
            case 'b':
                name = 'bold';
                onClick = () => editor.chain().focus().toggleBold().run();
                break;
            case 'i':
                name = 'italic';
                onClick = () => editor.chain().focus().toggleItalic().run();
                break;
            case 'u':
                name = 'underline';
                onClick = () => editor.chain().focus().toggleUnderline().run();
                break;
            case 'del':
                name = 'strikethrough';
                onClick = () => editor.chain().focus().toggleStrike().run();
                break;
            case 'ol':
                name = 'orderedList';
                onClick = () => editor.chain().focus().toggleOrderedList().run();
                break;
            case 'ul':
                name = 'unorderedList';
                onClick = () => editor.chain().focus().toggleBulletList().run();
                break;
            case 'mention':
                name = 'mention';
                onClick = () => editor.chain().focus().insertContent('@').run();
                break;
            case 'emoji':
                name = 'mood';
                break;
            default:
                break;
        }
        return {
            name,
            onClick,
            ...commonProps,
        };
    }, [color, item, editor, className]);

    const handlePopoverVisibility = useCallback(
        (visible) => {
            setPopoverVisible(visible);
            setShowingMenuPopover && setShowingMenuPopover(visible);
        },
        [setShowingMenuPopover],
    );

    switch (true) {
        case !editor:
            return null;
        case item === 'emoji':
            return (
                <EmojiMenu
                    popoverVisible={popoverVisible}
                    onVisibleChange={handlePopoverVisibility}
                    ref={emojiMenuRef}
                >
                    <Tooltip visible={!popoverVisible && visible} {...tooltipProps}>
                        <span
                            className={className}
                            onMouseEnter={() => setVisible(true)}
                            onMouseLeave={() => setVisible(false)}
                        >
                            <Icon {...iconProps} />
                        </span>
                    </Tooltip>
                </EmojiMenu>
            );
        default:
            return (
                <Tooltip {...tooltipProps}>
                    <span className={className}>
                        <Icon {...iconProps} />
                    </span>
                </Tooltip>
            );
    }
};

export default ToolbarItem;
